import React, { useState, useEffect, ChangeEvent, FormEvent } from "react";
import { useParams, useHistory } from "react-router-dom";
import { toast } from "react-toastify";

// Services & Types
import { EcodimeClassService } from "services/ecodime-class.service";
import {
  ISaveEcodimeClassDTO,
  IUpdateEcodimeClassDTO,
} from "models/church.types";

interface FormErrors {
  name?: string;
  ageMin?: string;
  ageMax?: string;
}

export default function EcodimeClassRegister() {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});

  const [ecodimeClass, setEcodimeClass] = useState<
    ISaveEcodimeClassDTO | IUpdateEcodimeClassDTO
  >({
    name: "",
    ageMin: "",
    ageMax: "",
  });

  useEffect(() => {
    const initData = async () => {
      if (isEditMode && id) {
        try {
          const data = await EcodimeClassService.getById(parseInt(id));
          setEcodimeClass({
            id: data.id,
            name: data.name,
            ageMin:
              data.ageMin !== null && data.ageMin !== undefined
                ? data.ageMin
                : "",
            ageMax:
              data.ageMax !== null && data.ageMax !== undefined
                ? data.ageMax
                : "",
          });
        } catch (error) {
          console.error("Erreur d'initialisation:", error);
          toast.error("Erreur lors de la récupération de la classe d'Écodime");
          history.push("/admin/ecodime-classes");
        }
      }
    };
    initData();
  }, [id, isEditMode, history]);

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (!ecodimeClass.name.trim()) {
      newErrors.name = "Le nom de la classe est obligatoire";
    }

    const min = ecodimeClass.ageMin !== "" ? Number(ecodimeClass.ageMin) : null;
    const max = ecodimeClass.ageMax !== "" ? Number(ecodimeClass.ageMax) : null;

    if (min !== null && (isNaN(min) || min < 0)) {
      newErrors.ageMin = "L'âge minimum doit être un nombre positif";
    }

    if (max !== null && (isNaN(max) || max < 0)) {
      newErrors.ageMax = "L'âge maximum doit être un nombre positif";
    }

    if (min !== null && max !== null && min > max) {
      newErrors.ageMax =
        "L'âge maximum doit être supérieur ou égal à l'âge minimum";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Pour les champs numériques, on conserve la chaîne vide ou on extrait le nombre
    setEcodimeClass((prev) => ({
      ...prev,
      [name]: name === "name" ? value : value === "" ? "" : Number(value),
    }));

    if (errors.hasOwnProperty(name)) {
      setErrors({ ...errors, [name]: undefined });
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);

    // Nettoyage des valeurs pour correspondre aux types attendus par le DTO Spring Boot (null si vide)
    const payload = {
      ...ecodimeClass,
      ageMin: ecodimeClass.ageMin === "" ? null : ecodimeClass.ageMin,
      ageMax: ecodimeClass.ageMax === "" ? null : ecodimeClass.ageMax,
    };

    try {
      if (isEditMode) {
        await EcodimeClassService.update(payload as IUpdateEcodimeClassDTO);
        toast.success("Classe d'Écodime mise à jour avec succès");
      } else {
        await EcodimeClassService.create(payload as ISaveEcodimeClassDTO);
        toast.success("Classe d'Écodime créée avec succès");
      }
      history.push("/admin/ecodime-classes");
    } catch (error) {
      console.error("Erreur lors de l'enregistrement:", error);
      toast.error("Erreur lors de l'enregistrement de la classe");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center p-4 w-full min-h-screen">
      <div className="w-full max-w-2xl">
        <div className="relative flex flex-col min-w-0 break-words w-full mb-6 rounded-xl bg-[#1E293B] border border-slate-700 shadow-2xl">
          {/* Header */}
          <div className="rounded-t-xl bg-[#0F172A] px-6 py-4 border-b border-slate-700">
            <div className="flex justify-between items-center">
              <h6 className="text-white text-sm font-bold uppercase tracking-widest">
                <i className="fas fa-graduation-cap mr-2 text-[#D4AF37]"></i>
                {isEditMode
                  ? "Modifier la classe d'Écodime"
                  : "Nouvelle Classe d'Écodime"}
              </h6>
              <button
                type="button"
                onClick={() => history.goBack()}
                className="text-slate-400 hover:text-white text-xs font-bold uppercase transition-all"
              >
                <i className="fas fa-times mr-1"></i> Fermer
              </button>
            </div>
          </div>

          {/* Form Body */}
          <div className="flex-auto px-6 py-8">
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-6">
                {/* Nom de la Classe */}
                <div>
                  <label className="text-[10px] text-[#D4AF37] uppercase font-black tracking-widest ml-1 mb-2 block">
                    <i className="fas fa-tag mr-2"></i>Nom de la classe
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={ecodimeClass.name}
                    onChange={handleChange}
                    placeholder="Ex: Petits Flambeaux, Cadets, Étoiles..."
                    className={`w-full bg-[#0F172A] border ${
                      errors.name ? "border-red-500" : "border-slate-700"
                    } rounded-lg px-4 py-3 text-white text-sm focus:border-[#D4AF37] outline-none transition-all`}
                  />
                  {errors.name && (
                    <p className="text-red-500 text-[10px] mt-1 italic">
                      {errors.name}
                    </p>
                  )}
                </div>

                {/* Tranche d'âges (Grid 2 Colonnes) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Âge Minimum */}
                  <div>
                    <label className="text-[10px] text-[#D4AF37] uppercase font-black tracking-widest ml-1 mb-2 block">
                      <i className="fas fa-arrow-down mr-2"></i>Âge Minimum
                      (ans)
                    </label>
                    <input
                      type="number"
                      name="ageMin"
                      min="0"
                      value={ecodimeClass.ageMin}
                      onChange={handleChange}
                      placeholder="Ex: 3"
                      className={`w-full bg-[#0F172A] border ${
                        errors.ageMin ? "border-red-500" : "border-slate-700"
                      } rounded-lg px-4 py-3 text-white text-sm focus:border-[#D4AF37] outline-none transition-all`}
                    />
                    {errors.ageMin && (
                      <p className="text-red-500 text-[10px] mt-1 italic">
                        {errors.ageMin}
                      </p>
                    )}
                  </div>

                  {/* Âge Maximum */}
                  <div>
                    <label className="text-[10px] text-[#D4AF37] uppercase font-black tracking-widest ml-1 mb-2 block">
                      <i className="fas fa-arrow-up mr-2"></i>Âge Maximum (ans)
                    </label>
                    <input
                      type="number"
                      name="ageMax"
                      min="0"
                      value={ecodimeClass.ageMax}
                      onChange={handleChange}
                      placeholder="Ex: 6"
                      className={`w-full bg-[#0F172A] border ${
                        errors.ageMax ? "border-red-500" : "border-slate-700"
                      } rounded-lg px-4 py-3 text-white text-sm focus:border-[#D4AF37] outline-none transition-all`}
                    />
                    {errors.ageMax && (
                      <p className="text-red-500 text-[10px] mt-1 italic">
                        {errors.ageMax}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Actions Formulaire */}
              <div className="mt-10 flex justify-end gap-4">
                <button
                  type="button"
                  onClick={() => history.push("/admin/ecodime-classes")}
                  className="px-6 py-3 rounded-lg font-bold text-xs uppercase text-slate-400 hover:text-white transition-all"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-[#D4AF37] text-[#0F172A] px-10 py-3 rounded-lg font-black text-xs uppercase tracking-widest hover:scale-105 transition-all shadow-lg disabled:opacity-50"
                >
                  {loading ? (
                    <i className="fas fa-spinner animate-spin mr-2"></i>
                  ) : (
                    <i className="fas fa-save mr-2"></i>
                  )}
                  {isEditMode ? "Mettre à jour" : "Créer la classe"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
